import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveIcon from '@mui/icons-material/Save'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import CustomButton from '../../global/CustomButton'

const defaultSettings = {
  contactsMessage: '',
  meetingTitle: '',
  meetingButtons: []
}

const normalizeSettings = (settings) => {
  const meetingButtons = Array.isArray(settings?.meetingButtons)
    ? settings.meetingButtons.map((button) => ({
        label: button?.label ?? '',
        url: button?.url ?? ''
      }))
    : []

  return {
    contactsMessage: settings?.contactsMessage ?? '',
    meetingTitle: settings?.meetingTitle ?? '',
    meetingButtons
  }
}

const isValidHttpUrl = (value) => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch (_) {
    return false
  }
}

const BotSettingsPanel = ({ jwt, backendUrl }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [formData, setFormData] = useState(defaultSettings)

  const loadSettings = () => {
    setLoading(true)
    setErrorMessage('')
    setSuccessMessage('')

    fetch(`${backendUrl}/telegram/bot-settings`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + jwt
      }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Could not load bot settings.')
        }
        const data = await response.json()
        const normalized = normalizeSettings(data)
        setFormData(normalized)
      })
      .catch((error) => {
        setErrorMessage(error.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    loadSettings()
    // eslint-disable-next-line
  }, [jwt, backendUrl])

  const handleInputChange = (event) => {
    setErrorMessage('')
    setSuccessMessage('')
    const { name, value } = event.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }))
  }

  const handleMeetingButtonChange = (index, key, value) => {
    setErrorMessage('')
    setSuccessMessage('')
    setFormData((prevData) => {
      const updatedButtons = [...prevData.meetingButtons]
      updatedButtons[index] = {
        ...updatedButtons[index],
        [key]: value
      }
      return {
        ...prevData,
        meetingButtons: updatedButtons
      }
    })
  }

  const handleAddMeetingButton = () => {
    setErrorMessage('')
    setSuccessMessage('')
    setFormData((prevData) => ({
      ...prevData,
      meetingButtons: [...prevData.meetingButtons, { label: '', url: '' }]
    }))
  }

  const handleRemoveMeetingButton = (index) => {
    setErrorMessage('')
    setSuccessMessage('')
    setFormData((prevData) => ({
      ...prevData,
      meetingButtons: prevData.meetingButtons.filter((_, idx) => idx !== index)
    }))
  }

  const handleSave = () => {
    setErrorMessage('')
    setSuccessMessage('')

    if (!formData.contactsMessage.trim()) {
      setErrorMessage('Contacts message can not be empty.')
      return
    }

    const normalizedButtons = formData.meetingButtons.map((button) => ({
      label: button.label.trim(),
      url: button.url.trim()
    }))

    const invalidButton = normalizedButtons.find(
      (button) => !button.label || !button.url || !isValidHttpUrl(button.url)
    )

    if (invalidButton) {
      setErrorMessage(
        'Every meeting button must have a label and a valid URL (http/https).'
      )
      return
    }

    setSaving(true)

    const payload = {
      contactsMessage: formData.contactsMessage.trim(),
      meetingTitle: formData.meetingTitle.trim(),
      meetingButtons: normalizedButtons
    }

    fetch(`${backendUrl}/telegram/bot-settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + jwt
      },
      body: JSON.stringify(payload)
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null)
        if (!response.ok) {
          const backendError = Array.isArray(data?.message)
            ? data.message.join(', ')
            : data?.message
          throw new Error(
            backendError || 'There was an error updating bot settings.'
          )
        }

        const normalized = normalizeSettings(data)
        setFormData(normalized)
        setSuccessMessage('Bot settings were saved.')
      })
      .catch((error) => {
        setErrorMessage(error.message)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <Paper
      sx={{
        p: '24px',
        borderRadius: '16px',
        boxShadow:
          'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px'
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography variant="h4">Bot Settings</Typography>
            <Typography color="text.secondary">
              Manage the Contacts message and Book a meeting options for Telegram
              bot menus.
            </Typography>
          </Box>
          <CustomButton
            buttonText={saving ? 'Saving...' : 'Save'}
            icon={SaveIcon}
            color="black"
            onClick={handleSave}
            disabled={saving || loading}
          />
        </Stack>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: '30px'
            }}
          >
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="h6">Contacts Message (Markdown)</Typography>
              <TextField
                name="contactsMessage"
                value={formData.contactsMessage}
                onChange={handleInputChange}
                multiline
                minRows={8}
                fullWidth
                helperText="This text is shown when a user clicks “Contacts” in the Telegram bot."
              />
            </Stack>

            <Divider />
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Typography variant="h6">Book a Meeting</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddMeetingButton}
                >
                  Add Button
                </Button>
              </Stack>

              <TextField
                name="meetingTitle"
                label="Meeting section title"
                value={formData.meetingTitle}
                onChange={handleInputChange}
                fullWidth
              />

              {formData.meetingButtons.length === 0 ? (
                <Alert severity="info">
                  No meeting buttons configured. Add buttons with label + URL.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {formData.meetingButtons.map((button, index) => (
                    <Grid item xs={12} key={`meeting-button-${index}`}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <TextField
                          label="Button label"
                          value={button.label}
                          onChange={(event) =>
                            handleMeetingButtonChange(
                              index,
                              'label',
                              event.target.value
                            )
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Meeting URL"
                          value={button.url}
                          onChange={(event) =>
                            handleMeetingButtonChange(
                              index,
                              'url',
                              event.target.value
                            )
                          }
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveMeetingButton(index)}
                          aria-label={`Remove meeting button ${index + 1}`}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  )
}

export default BotSettingsPanel
